import React, { useState, useEffect, useMemo } from "react";
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { dashboardAPI } from "../../services/api";

const AISubmissionsView = ({ activeConfigurationId }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    field: "created_at",
    direction: "desc",
  });
  const [filters, setFilters] = useState({
    username: "",
    ai_model_name: "",
  });

  useEffect(() => {
    if (activeConfigurationId) {
      loadSubmissions();
    } else {
      setSubmissions([]);
      setLoading(false);
    }
  }, [activeConfigurationId]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await dashboardAPI.getAISubmissions(
        activeConfigurationId,
      );
      setSubmissions(response.data || []);
    } catch (err) {
      console.error("Failed to load AI submissions:", err);
      setError(err.response?.data?.error || "Failed to load AI submissions");
    } finally {
      setLoading(false);
    }
  };

  // Get unique values for filter dropdowns
  const uniqueUsernames = useMemo(() => {
    const usernames = [
      ...new Set(submissions.map((s) => s.username).filter(Boolean)),
    ];
    return usernames.sort();
  }, [submissions]);

  const uniqueModels = useMemo(() => {
    const models = [
      ...new Set(submissions.map((s) => s.ai_model_name).filter(Boolean)),
    ];
    return models.sort();
  }, [submissions]);

  // Filter and sort submissions
  const filteredAndSortedSubmissions = useMemo(() => {
    let filtered = submissions;

    // Apply search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (sub) =>
          sub.title?.toLowerCase().includes(searchLower) ||
          sub.username?.toLowerCase().includes(searchLower) ||
          sub.email?.toLowerCase().includes(searchLower) ||
          sub.ai_model_name?.toLowerCase().includes(searchLower) ||
          sub.configuration_name?.toLowerCase().includes(searchLower),
      );
    }

    // Apply column filters
    if (filters.username) {
      filtered = filtered.filter((sub) => sub.username === filters.username);
    }
    if (filters.ai_model_name) {
      filtered = filtered.filter(
        (sub) => sub.ai_model_name === filters.ai_model_name,
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aValue = a[sortConfig.field];
      let bValue = b[sortConfig.field];

      // Handle null/undefined values
      if (aValue == null) aValue = "";
      if (bValue == null) bValue = "";

      // Handle date fields
      if (
        sortConfig.field === "created_at" ||
        sortConfig.field === "updated_at"
      ) {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      // Handle numeric fields
      if (
        sortConfig.field === "input_tokens" ||
        sortConfig.field === "output_tokens"
      ) {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }

      // Compare values
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [submissions, searchTerm, filters, sortConfig]);

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatNumber = (num) => {
    if (num == null) return "N/A";
    return Number(num).toLocaleString();
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        AI Submissions Summary
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View all AI analysis submissions with token usage and model information
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
          <InputLabel>User</InputLabel>
          <Select
            value={filters.username}
            onChange={(e) =>
              setFilters({ ...filters, username: e.target.value })
            }
            label="User"
          >
            <MenuItem value="">All Users</MenuItem>
            {uniqueUsernames.map((username) => (
              <MenuItem key={username} value={username}>
                {username}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
          <InputLabel>AI Model</InputLabel>
          <Select
            value={filters.ai_model_name}
            onChange={(e) =>
              setFilters({ ...filters, ai_model_name: e.target.value })
            }
            label="AI Model"
          >
            <MenuItem value="">All Models</MenuItem>
            {uniqueModels.map((model) => (
              <MenuItem key={model} value={model}>
                {model}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ flexGrow: 1 }} />
        <Chip
          label={`${filteredAndSortedSubmissions.length} submission(s)`}
          color="primary"
          variant="outlined"
        />
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.field === "title"}
                  direction={
                    sortConfig.field === "title" ? sortConfig.direction : "asc"
                  }
                  onClick={() => handleSort("title")}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.field === "username"}
                  direction={
                    sortConfig.field === "username"
                      ? sortConfig.direction
                      : "asc"
                  }
                  onClick={() => handleSort("username")}
                >
                  User
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.field === "created_at"}
                  direction={
                    sortConfig.field === "created_at"
                      ? sortConfig.direction
                      : "asc"
                  }
                  onClick={() => handleSort("created_at")}
                >
                  Submitted At
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortConfig.field === "input_tokens"}
                  direction={
                    sortConfig.field === "input_tokens"
                      ? sortConfig.direction
                      : "asc"
                  }
                  onClick={() => handleSort("input_tokens")}
                >
                  Input Tokens
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortConfig.field === "output_tokens"}
                  direction={
                    sortConfig.field === "output_tokens"
                      ? sortConfig.direction
                      : "asc"
                  }
                  onClick={() => handleSort("output_tokens")}
                >
                  Output Tokens
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Total Tokens</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.field === "ai_model_name"}
                  direction={
                    sortConfig.field === "ai_model_name"
                      ? sortConfig.direction
                      : "asc"
                  }
                  onClick={() => handleSort("ai_model_name")}
                >
                  AI Model
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedSubmissions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {submissions.length === 0
                      ? "No AI submissions found"
                      : "No submissions match your search criteria"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedSubmissions.map((submission) => {
                const totalTokens =
                  (Number(submission.input_tokens) || 0) +
                  (Number(submission.output_tokens) || 0);
                return (
                  <TableRow key={submission.id} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 300,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {submission.title || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {submission.username || "N/A"}
                        </Typography>
                        {submission.email && (
                          <Typography variant="caption" color="text.secondary">
                            {submission.email}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(submission.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatNumber(submission.input_tokens)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatNumber(submission.output_tokens)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {formatNumber(totalTokens)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={submission.ai_model_name || "N/A"}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default AISubmissionsView;
